import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    LinearProgress,
    Grid,
} from "@mui/material";
import { Edit, Close, CloudUpload, Person, Email } from "@mui/icons-material";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import en_ja from "../../translations/en_ja";

const nameRegex = /^[\p{L}\s]+$/u;

const AdminProfile = () => {
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);

    const [profile, setProfile] = useState({
        name: "",
        email: "",
        profile_image: "",
    });

    const [editFormData, setEditFormData] = useState({
        name: "",
        profile_image: null,
    });

    const [previewImage, setPreviewImage] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    const [originalProfile, setOriginalProfile] = useState(null);

    const [nameError, setNameError] = useState("");

    const user = React.useMemo(() => {
        try {
            const raw = localStorage.getItem("user");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    // check super admin
    const roleName = user?.role?.roleName;
    const isSuperAdmin =
        typeof roleName === "string" && roleName.trim().toLowerCase() === "super admin";

    const validateName = (value) => {
        if (!value.trim()) {
            return en_ja["Name is required"];
        }

        if (!nameRegex.test(value.trim())) {
            return en_ja["Only letters are allowed"];
        }

        return "";
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const user = JSON.parse(localStorage.getItem("user") || "null");

                if (!user?.id) {
                    setLoading(false);
                    return;
                }

                const res = await api.get(`/v1/admin/profile/${user.id}`);

                if (res.data?.data) {
                    const admin = res.data.data;

                    setProfile({
                        name: admin.name || "",
                        email: admin.email || "",
                        profile_image: admin.profile_image || "",
                    });
                }
            } catch (err) {
                console.error("Fetch profile failed:", err);
                setSnackbarMessage(en_ja["Failed to load profile"]);
                setSnackbarType("error");
                setSnackbarOpen(true);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const handleEditProfile = () => {
        setOriginalProfile({
            name: profile.name,
            profile_image: profile.profile_image,
        });

        setEditFormData({
            name: profile.name,
            profile_image: null,
        });

        setPreviewImage(profile.profile_image || null);
        setNameError("");
        setEditDialogOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };

    const handleFieldChange = (field, value) => {
        if (field === "name") {
            const err = validateName(value);
            setNameError(err);
        }

        setEditFormData({ ...editFormData, [field]: value });
    };

    const handleImageChange = (file) => {
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => setPreviewImage(e.target.result);
            reader.readAsDataURL(file);
            setEditFormData({ ...editFormData, profile_image: file });
        }
    };

    const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
    const onFileDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) handleImageChange(files[0]);
    };
    const onFileInput = (e) => { if (e.target.files[0]) handleImageChange(e.target.files[0]); };

    const hasChanges = () => {
        if (!originalProfile) return false;

        const nameChanged = editFormData.name !== originalProfile.name;
        const imageChanged = !!editFormData.profile_image;

        return nameChanged || imageChanged;
    };

    const handleUpdateProfile = async () => {
        try {
            if (!hasChanges()) return;

            const nameErr = validateName(editFormData.name);
            if (nameErr) {
                setNameError(nameErr);
                return;
            }

            const user = JSON.parse(localStorage.getItem("user") || "null");
            if (!user?.id) return;

            const formData = new FormData();
            formData.append("name", editFormData.name.trim());

            if (editFormData.profile_image) {
                formData.append("profile_image", editFormData.profile_image);
            }

            setUpdateLoading(true);

            const response = await api.put(
                `/v1/admin/edit/profile/${user.id}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            const admin = response.data?.admin;

            if (admin) {
                const updatedProfile = {
                    name: admin.name || "",
                    email: admin.email || "",
                    profile_image: admin.profile_image || "",
                };

                setProfile(updatedProfile);

                setOriginalProfile(null);
                setEditFormData({ name: "", profile_image: null });

                setEditDialogOpen(false);
                setSnackbarMessage(en_ja["Profile updated successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
            }

        } catch (err) {
            console.error("Error updating profile:", err);
            setSnackbarMessage(en_ja["Error updating profile"]);
            setSnackbarType("error");
            setSnackbarOpen(true);
        } finally {
            setUpdateLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <LinearProgress />
                <Typography sx={{ mt: 2, textAlign: "center" }}>
                    {en_ja["Loading profile..."]}
                </Typography>
            </Box>
        );
    }

    const handleCloseDialog = () => {
        setEditDialogOpen(false);
        setEditFormData({
            name: profile.name,
            profile_image: null,
        });
        setPreviewImage(profile.profile_image || null);
        setNameError("");
    };

    return (
        <Box sx={{ p: 3, minHeight: "100vh", backgroundColor: "background.default" }}>

            <CustomSnackbar
                isOpen={snackbarOpen}
                message={snackbarMessage}
                onClose={handleCloseSnackbar}
                type={snackbarType}
            />

            <Paper elevation={0} sx={{ borderRadius: "16px", overflow: "hidden" }}>
                <Box
                    sx={{
                        p: 4,
                        background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Box sx={{ position: "relative" }}>
                            <Avatar
                                src={profile.profile_image}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    fontSize: "28px",
                                    fontWeight: "600",
                                    backgroundColor: "#76AEDE",
                                    color: "white",
                                    boxShadow: "0 8px 24px rgba(118, 174, 222, 0.3)",
                                    border: "4px solid white",
                                }}>
                                {!profile.profile_image && profile.name?.[0]}
                            </Avatar>

                            {!isSuperAdmin && (
                                <IconButton
                                    onClick={handleEditProfile}
                                    sx={{
                                        position: "absolute", bottom: -8, right: -8,
                                        backgroundColor: "white", color: "#76AEDE",
                                        border: "2px solid #76AEDE", width: 40, height: 40,
                                        "&:hover": { backgroundColor: "#76AEDE", color: "white", transform: "scale(1.05)" },
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)", transition: "all 0.2s ease",
                                    }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>)}
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" fontWeight="700" sx={{ fontSize: "28px", mb: 1 }}>
                                {profile.name}
                            </Typography>

                            {!isSuperAdmin && (<Typography variant="body1" color="text.secondary" sx={{ fontSize: "16px", mb: 2 }}>
                                {profile.email}
                            </Typography>)}

                            {!isSuperAdmin && (<Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={handleEditProfile}
                                sx={{
                                    borderColor: "#76AEDE", color: "#76AEDE",
                                    "&:hover": { backgroundColor: "#76AEDE", color: "white" },
                                    transition: "all 0.2s ease", px: 3, py: 1,
                                }}
                            >
                                {en_ja["Edit Profile"]}
                            </Button>)}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ p: 4 }}>
                    <Grid container spacing={6}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                                <Person sx={{ color: "#76AEDE", fontSize: "20px" }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                                        {en_ja["Full Name"]}
                                    </Typography>
                                    <Typography variant="body1" color="text.primary" sx={{ fontWeight: "500" }}>
                                        {profile.name}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {!isSuperAdmin && (<Grid item xs={12} md={6}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                                <Email sx={{ color: "#76AEDE", fontSize: "20px" }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "500" }}>
                                        {en_ja["Email"]}
                                    </Typography>
                                    <Typography variant="body1" color="text.primary" sx={{ fontWeight: "500" }}>
                                        {profile.email}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>)}
                    </Grid>
                </Box>
            </Paper>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6" fontWeight="600">
                            {en_ja["Edit Profile"]}
                        </Typography>
                        <IconButton onClick={handleCloseDialog} sx={{ color: "text.secondary" }}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, paddingTop: "24px !important", p: 0 }}>
                    <Box sx={{ px: 3, overflowY: "auto", pt: 2 }}>
                        <TextField
                            label={en_ja["Name"]}
                            value={editFormData.name}
                            error={!!nameError}
                            helperText={nameError}
                            onChange={(e) => handleFieldChange("name", e.target.value)}
                            fullWidth
                            sx={{ mb: 3 }}
                        />

                        <TextField
                            label={en_ja["Email"]}
                            value={profile.email}
                            fullWidth
                            disabled
                            sx={{ mb: 3 }}
                        />

                        <Box sx={{ pb: 5 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                {en_ja["Upload Image"]}
                            </Typography>

                            {previewImage && (
                                <Box sx={{ mb: 2, textAlign: "center" }}>
                                    <Avatar src={previewImage} sx={{ width: 80, height: 80, mx: "auto", mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {en_ja["Current Image"]}
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onFileDrop}
                                sx={{
                                    border: "2px dashed",
                                    borderColor: dragOver ? "#76AEDE" : "#e0e0e0",
                                    borderRadius: "12px",
                                    p: 6,
                                    textAlign: "center",
                                    backgroundColor: dragOver ? "rgba(118,174,222,0.05)" : "transparent",
                                    cursor: "pointer",
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <CloudUpload sx={{ fontSize: 48, color: dragOver ? "#76AEDE" : "text.secondary", mb: 2 }} />
                                <Typography>{en_ja["Drag and drop the image or"]}</Typography>
                                <Button variant="text" sx={{ color: "#76AEDE", textTransform: "none" }}>
                                    {en_ja["Choose Image"]}
                                </Button>
                            </Box>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={onFileInput}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: "text.secondary",
                            "&:hover": { backgroundColor: "error.light", color: "white" },
                        }}
                    >
                        {en_ja["Cancel"]}
                    </Button>

                    <Button
                        onClick={handleUpdateProfile}
                        variant="contained"
                        sx={{ backgroundColor: "#ef5350", "&:hover": { backgroundColor: "#d32f2f" }, px: 4 }}
                        disabled={updateLoading || !hasChanges() || !!nameError}
                    >
                        {updateLoading ? en_ja["Updating..."] : en_ja["Update profile"]}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminProfile;
